import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Search, Filter, Eye, Trash2, Clock, AlertTriangle } from "lucide-react";

const STATUSES = ["New Case", "Work in Process", "Sent for Approval", "Approved", "Completed", "On Hold", "Cancel", "Document Pending", "Chalan Pending"];
const DEFAULT_SERVICES = ["ITR", "GST", "MSME Loan", "Business Loan", "Personal Loan", "Insurance", "Other"];
const SLA_OPTIONS = ["On Time", "Due Soon", "Overdue"];

const slaBadge = (s) => ({ "On Time": "badge-green", "Due Soon": "badge-yellow", "Overdue": "badge-red" }[s] || "badge-gray");

export default function Leads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [serviceType, setServiceType] = useState(searchParams.get("service") || "");
  const [sla, setSla] = useState(searchParams.get("sla") || "");
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const canAssign = isAdmin || (user?.role === "employee" && perms.includes("assign_leads"));
  useEffect(() => { api.get("/meta").then((r) => setServices(r.data.service_types)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (serviceType) params.service_type = serviceType;
    if (sla) params.sla = sla;
    try {
      const r = await api.get("/leads", { params });
      setLeads(r.data);
    } finally { setLoading(false); }
  };

  const deleteLead = async (e, lead) => {
    e.stopPropagation(); e.preventDefault();
    if (!window.confirm(`Delete ${lead.lead_number} — ${lead.client_name}?\nThis will also remove all uploaded documents. This cannot be undone.`)) return;
    try { await api.delete(`/leads/${lead.id}`); load(); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to delete lead"); }
  };

  const assignLead = async (e, lead, employeeId) => {
    e.stopPropagation();
    await api.patch(`/leads/${lead.id}`, { assigned_to_id: employeeId });
    load();
  };

  useEffect(() => {
    // Load employees list only if the current user can actually assign leads.
    if (canAssign) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    }
  }, [canAssign]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [serviceType, status, sla]);

  const setSp = (key, value) => {
    const p = Object.fromEntries(searchParams);
    if (value) p[key] = value; else delete p[key];
    setSearchParams(p);
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">{user?.role === "admin" ? "All Leads" : "My Orders"}</h1>
          <p className="text-sm text-muted-foreground">Track and manage every lead in the pipeline</p>
        </div>
        {user?.role === "connector" && (
          <Link to="/new-order" className="btn btn-primary" data-testid="btn-new-order-cta">+ New Order</Link>
        )}
      </div>

      <div className="card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by client, phone, email, PAN, Lead ID, connector…"
            className="input pl-9"
            data-testid="leads-search-input"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={16} className="text-muted-foreground" />
          <select className="input !w-auto" value={status} onChange={(e) => { setStatus(e.target.value); setSp("status", e.target.value); }} data-testid="leads-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="input !w-auto" value={serviceType} onChange={(e) => { setServiceType(e.target.value); setSp("service", e.target.value); }} data-testid="leads-service-filter">
            <option value="">All services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="input !w-auto" value={sla} onChange={(e) => { setSla(e.target.value); setSp("sla", e.target.value); }} data-testid="leads-sla-filter">
            <option value="">All SLA</option>
            {SLA_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={load} data-testid="leads-apply-filter-btn">Apply</button>
        <button className="btn btn-outline" onClick={() => { setQ(""); setStatus(""); setServiceType(""); setSla(""); setSearchParams({}); setTimeout(load, 10); }} data-testid="leads-reset-filter-btn">Reset</button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-clean">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Lead ID</th>
                <th>Service</th>
                <th>Date</th>
                <th>Client Name</th>
                <th>Phone</th>
                {isAdmin && <th>Connector</th>}
                {canAssign && <th>Assign To</th>}
                <th>Status</th>
                <th>SLA</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="leads-table-body">
              {(() => {
                const colSpan = 9 + (isAdmin ? 1 : 0) + (canAssign ? 1 : 0);
                if (loading) return <tr><td colSpan={colSpan} className="text-center text-muted-foreground py-8">Loading…</td></tr>;
                if (leads.length === 0) return <tr><td colSpan={colSpan} className="text-center text-muted-foreground py-8">No leads found</td></tr>;
                return null;
              })()}
              {!loading && leads.length > 0 && leads.map((l, i) => (
                <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className="cursor-pointer" data-testid={`lead-row-${l.lead_number}`}>
                  <td className="tabular text-muted-foreground">{i + 1}</td>
                  <td><Link to={`/leads/${l.id}`} onClick={(e) => e.stopPropagation()} className="font-mono font-semibold link-blue" data-testid={`lead-link-${l.lead_number}`}>{l.lead_number}</Link></td>
                  <td><span className="badge badge-blue">{l.service_type || "Other"}</span></td>
                  <td className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="font-semibold">{l.client_name}</td>
                  <td className="tabular">{l.phone}</td>
                  {isAdmin && <td>{l.connector_name}<div className="text-xs text-muted-foreground">{l.connector_phone || l.connector_email || "—"}</div></td>}
                  {canAssign && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="input !py-1 !text-xs !w-[130px]"
                        value={l.assigned_to_id || ""}
                        onChange={(e) => assignLead(e, l, e.target.value)}
                        data-testid={`assign-select-${l.lead_number}`}
                      >
                        <option value="">Unassigned</option>
                        {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                      </select>
                    </td>
                  )}
                  <td><StatusBadge status={l.status} /></td>
                  <td>
                    <span className={`badge ${slaBadge(l.sla_status)}`} title={l.hours_remaining >= 0 ? `${l.hours_remaining}h left` : `${Math.abs(l.hours_remaining)}h over`}>
                      {l.sla_status === "Overdue" ? <AlertTriangle size={10} className="mr-1" /> : <Clock size={10} className="mr-1" />}
                      {l.sla_status || "—"}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/leads/${l.id}`} className="btn btn-ghost !p-2 inline-flex" title="View" data-testid={`lead-view-${l.lead_number}`}>
                      <Eye size={16} />
                    </Link>
                    {user?.role === "admin" && (
                      <button onClick={(e) => deleteLead(e, l)} className="btn btn-ghost !p-2 text-red-600 ml-1" title="Delete lead" data-testid={`lead-delete-${l.lead_number}`}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
